// oxlint-disable react/only-export-components
import { DropdownMenu as DropdownMenuPrimitive, Slot } from "radix-ui"
import { createContext, useContext } from "react"
import type { ComponentProps, MouseEvent, ReactNode } from "react"
import { Drawer as DrawerPrimitive } from "vaul"
import { cn } from "cn"

import { useIsMobile } from "../../../hooks/use-mobile"
import {
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "../drawer/drawer"

/**
 * Figma „Dropdown" besteht aus „Dropdown Menu Item" (Desktop und Mobile
 * identisch), „Dropdown Menu List" (Desktop-Panel) und einem Mobile-Drawer
 * mit denselben Items. Unterhalb des `md`-Breakpoints (< 768 px) schaltet
 * `DropdownMenu` daher automatisch auf den V2-`Drawer` um (analog zu
 * `Popover`) — die Prop-API der Bausteine bleibt identisch.
 *
 * Auf Mobile gibt es keine Radix-Menü-Navigation (Typeahead, Pfeiltasten) und
 * keine Untermenüs — diese sind nicht Teil des Designs und nicht umgesetzt.
 */
const DropdownMenuIsMobileContext = createContext(false)

function DropdownMenu({
  children,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  const isMobile = useIsMobile()

  if (isMobile) {
    // Radix-`modal`/`dir` etc. gibt es am Drawer nicht — nur open-State durchreichen.
    const { open, defaultOpen, onOpenChange } = props
    return (
      <DropdownMenuIsMobileContext.Provider value>
        <DrawerPrimitive.Root
          data-slot="dropdown-menu"
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
        >
          {children}
        </DrawerPrimitive.Root>
      </DropdownMenuIsMobileContext.Provider>
    )
  }

  return (
    <DropdownMenuIsMobileContext.Provider value={false}>
      <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props}>
        {children}
      </DropdownMenuPrimitive.Root>
    </DropdownMenuIsMobileContext.Provider>
  )
}

function DropdownMenuTrigger({
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  const isMobile = useContext(DropdownMenuIsMobileContext)

  return isMobile ? (
    <DrawerPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />
  ) : (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      {...props}
    />
  )
}

type DropdownMenuContentProps = ComponentProps<
  typeof DropdownMenuPrimitive.Content
> & {
  /** Titel im Drawer-Header (nur Mobile, Figma: „Aktionen"). */
  mobileTitle?: string
}

function DropdownMenuContent({
  className,
  children,
  sideOffset = 4,
  align = "start",
  mobileTitle = "Aktionen",
  ...props
}: DropdownMenuContentProps) {
  const isMobile = useContext(DropdownMenuIsMobileContext)

  if (isMobile) {
    return (
      <DrawerContent data-slot="dropdown-menu-content" className="gap-3">
        <DrawerHeader>
          <DrawerTitle>{mobileTitle}</DrawerTitle>
        </DrawerHeader>
        <div
          role="menu"
          className={cn("flex w-full flex-col gap-0.5", className)}
        >
          {children}
        </div>
      </DrawerContent>
    )
  }

  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        align={align}
        className={cn(
          "z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-56 overflow-x-hidden overflow-y-auto rounded-xl bg-backgrounds-primary shadow-popover outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          className
        )}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  )
}

/** Figma „Section": eine Gruppe von Items (`py-1`, 2 px Abstand). */
function DropdownMenuGroup({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return (
    <DropdownMenuPrimitive.Group
      data-slot="dropdown-menu-group"
      className={cn("flex flex-col gap-0.5 py-1", className)}
      {...props}
    />
  )
}

type DropdownMenuItemProps = ComponentProps<
  typeof DropdownMenuPrimitive.Item
> & {
  leftIcon?: ReactNode
  showLeftIcon?: boolean
  showDot?: boolean
  dotClassName?: string
  /** Inhalt des roten Zähler-Badges (Figma „Indicator", z. B. `1`). */
  badge?: ReactNode
  showBadge?: boolean
  rightIcon?: ReactNode
  showRightIcon?: boolean
}

/**
 * Figma unterscheidet bei den Items den State „hover" nur über Hintergrund
 * und **ohne** Radius (der Default hat `rounded-lg`) — als 1:1 aus dem Design
 * übernommen, daher `data-highlighted:rounded-none`. `state="disabled"` und
 * `state="hover"` sind keine Props, sondern `disabled` bzw. Zeiger/Tastatur.
 *
 * Ein destruktives Item (rote Schrift, Mobile-Beispiel „Löschen") ist keine
 * Figma-Prop, sondern per `className="text-error-red-200"` zu setzen.
 */
const itemClassName =
  "flex w-full cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-left text-sm leading-5 text-labels-primary outline-none select-none data-disabled:pointer-events-none data-disabled:text-labels-tertiary data-highlighted:rounded-none data-highlighted:bg-neutrals-v2-200"

function DropdownMenuItem({
  className,
  leftIcon,
  showLeftIcon = false,
  showDot = false,
  dotClassName,
  badge,
  showBadge = false,
  rightIcon,
  showRightIcon = false,
  children,
  asChild,
  disabled,
  onSelect,
  ...props
}: DropdownMenuItemProps) {
  const isMobile = useContext(DropdownMenuIsMobileContext)

  const content = (
    <>
      {showLeftIcon && leftIcon}
      {showDot && (
        <span
          className={cn(
            "size-2.5 shrink-0 rounded-full bg-brand-red-200",
            dotClassName
          )}
        />
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {showBadge && (
        <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary-red-400 px-1 py-0.5 text-xs leading-4 font-semibold text-neutrals-v2-100">
          {badge}
        </span>
      )}
      {showRightIcon && rightIcon}
    </>
  )

  if (isMobile) {
    const Comp = asChild ? Slot.Root : "button"
    // Radix-`onSelect`-Kontrakt: `preventDefault()` verhindert das Schließen.
    function handleClick(event: MouseEvent<HTMLElement>) {
      const selectEvent = new Event("menu.itemSelect", { cancelable: true })
      onSelect?.(selectEvent)
      if (selectEvent.defaultPrevented) event.preventDefault()
    }

    return (
      <DrawerClose asChild>
        <Comp
          data-slot="dropdown-menu-item"
          role="menuitem"
          type={asChild ? undefined : "button"}
          disabled={disabled}
          data-disabled={disabled ? "" : undefined}
          className={cn(
            itemClassName,
            "active:rounded-none active:bg-neutrals-v2-200",
            className
          )}
          onClick={handleClick}
          {...(props as unknown as ComponentProps<"button">)}
        >
          {asChild ? children : content}
        </Comp>
      </DrawerClose>
    )
  }

  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      asChild={asChild}
      disabled={disabled}
      onSelect={onSelect}
      className={cn(itemClassName, className)}
      {...props}
    >
      {asChild ? children : content}
    </DropdownMenuPrimitive.Item>
  )
}

/** Figma „Dropdown Menu Item" `type=section title` (14 px semibold) — für die kompaktere Variante aus der Liste (12 px bold) per `className` überschreiben. */
function DropdownMenuLabel({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn(
        "flex w-full items-center gap-2 px-4 py-2 text-sm leading-5 font-semibold text-labels-primary",
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("h-px w-full bg-separators-non-opaque", className)}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
}
export type { DropdownMenuContentProps, DropdownMenuItemProps }
